import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiAlertTriangle, FiInfo, FiX, FiAlertCircle } = FiIcons;

// Toast context and provider
const ToastContext = React.createContext();

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now().toString();
    const newToast = { id, duration: 3000, ...toast };
    setToasts(prev => [...prev, newToast]);

    // Auto remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const value = { addToast, removeToast, toasts };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  const { addToast } = context;

  return {
    toast: (message, options = {}) => addToast({ message, type: 'info', ...options }),
    success: (message, options = {}) => addToast({ message, type: 'success', ...options }),
    error: (message, options = {}) => addToast({ message, type: 'error', ...options }),
    warning: (message, options = {}) => addToast({ message, type: 'warning', ...options })
  };
}

function ToastContainer({ toasts, onRemove }) {
  return (
    <div className="fixed bottom-4 right-4 z-60 space-y-2 max-w-sm w-full">
      <AnimatePresence>
        {toasts.map(toast => (
          <Toast key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function Toast({ toast, onRemove }) {
  const variants = {
    success: { icon: FiCheck, bg: 'bg-sage', border: 'border-sage' },
    error: { icon: FiAlertCircle, bg: 'bg-crimson', border: 'border-crimson' },
    warning: { icon: FiAlertTriangle, bg: 'bg-goldenrod', border: 'border-goldenrod' },
    info: { icon: FiInfo, bg: 'bg-skyblue', border: 'border-skyblue' }
  };
  const config = variants[toast.type] || variants.info;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`
        ${config.bg} text-white px-4 py-3 rounded-xl shadow-lg
        border ${config.border} backdrop-blur-sm flex items-center gap-3 min-w-0
      `}
    >
      <SafeIcon icon={config.icon} className="w-5 h-5 flex-shrink-0" />
      <span className="font-dm-sans font-medium flex-1 min-w-0 break-words">
        {toast.message}
      </span>
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => onRemove(toast.id)}
        className="text-white/80 hover:text-white transition-colors p-1 rounded"
      >
        <SafeIcon icon={FiX} className="w-4 h-4" />
      </motion.button>
    </motion.div>
  );
}

export default Toast;